import { Input, Component, OnChanges, ViewEncapsulation, SimpleChanges, OnDestroy } from '@angular/core';
import { toLower } from 'lodash';
import { Subject } from 'rxjs';
import { first, map, tap, finalize, takeUntil } from 'rxjs/operators';
import * as moment from 'moment';

import { MeterDataService } from '@app/shared/services/api/meter-data.service';
import { ApplicationInsightEvent } from '@app/shared/enums';
import { AuthService } from '@app/shared/services/auth.service';
import { AppInsightsServiceDirective } from '@app/shared/services/app-insights.service';
import { BuildingTypesService } from '@app/shared/services/api/building-types.services';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { BuildingTypes } from '@app/shared/models/building-types';
import { EnergyMapBuilding } from '@app/shared/models/energiekarte/energy-map-building';
import { EnergyMapMeter } from '@app/shared/models/energiekarte/energy-map-meter';

@Component({
  selector: 'app-meter-list',
  templateUrl: './meter-list.component.html',
  styleUrls: ['./meter-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MeterListComponent implements OnChanges, OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();

  @Input() data: EnergyMapBuilding = {} as EnergyMapBuilding;

  public isMeterListLoading = true;
  public buildingTypes: BuildingTypes[] = [];
  private undefinedObjectType = {
    guid: '',
    name: '',
  } as BuildingTypes;

  constructor(
    public authService: AuthService,
    private appInsightsServiceDirective: AppInsightsServiceDirective,
    private buildingTypeService: BuildingTypesService,
    private meterDataService: MeterDataService,
    private userMunicipalitiesService: UserMunicipalitiesService
  ) {
    this.buildingTypeService
      .getAll()
      .pipe(
        tap(() => (this.isMeterListLoading = true)),
        map((buildings: BuildingTypes[]): BuildingTypes[] => {
          buildings = buildings.filter((building: BuildingTypes) => building.name);
          const sortedBuildings = buildings.sort((a, b) => a.name.localeCompare(b.name));
          return sortedBuildings;
        }),
        finalize(() => (this.isMeterListLoading = false)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((buildings: BuildingTypes[]) => (this.buildingTypes = buildings));
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.data.meters.forEach((meter: EnergyMapMeter) => {
      meter.isEdit = false;
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.data.meters.forEach((meter: EnergyMapMeter) => {
        meter.buildingType =
          this.buildingTypes.find((bt: BuildingTypes) => toLower(bt.name).trim() === toLower(meter.objectType).trim()) ?? this.undefinedObjectType;
      });
    }
  }

  public trackByFn(_: number, item: any): string {
    return item.guid;
  }

  public save(meter: EnergyMapMeter): void {
    if (toLower(meter.objectType).trim() !== toLower(meter.buildingType.name).trim()) {
      this.meterDataService
        .updateMeter({
          guid: meter.guid,
          objectType: meter.buildingType.name,
          objectTypeModifiedBy: this.authService.currentUser.guid,
          objectTypeModifyDate: moment().utc(),
          municipalityKey: this.userMunicipalitiesService.currentMunicipality.key,
        })
        .pipe(first())
        .subscribe(() => {
          meter.objectType = meter.buildingType.name;
          meter.isEdit = false;

          this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.meterObjectBuildingTypeSave, {
            buildingType: meter.buildingType.name,
            municipalityName: this.data.municipality,
            roleEdited: this.authService.currentUser.roleName,
          });
        });
    }
  }
}
